// Taking a payment while the customer is on the phone (F-40): the office never hears a card number;
// it texts or emails a pay link (payments.sendPayLink) and watches the invoice pay, or records money
// already in hand (payments.record). These read customers.money and the customer's contact methods.

import type { ContactMethod, Customer } from '../types.ts';
import type { Money } from '../history/history-model.ts';

type Invoice = Money['invoices'][number];
type Payment = NonNullable<Money['payments']>[number];

/** What can be paid now: an invoice with something owing that isn't void or written off. */
export const openInvoices = (m: Pick<Money, 'invoices'>): Invoice[] =>
  m.invoices.filter((i) => i.balanceCents > 0 && i.status !== 'void' && i.status !== 'written_off');

/** Where a pay link may go: a phone that agreed to texts, or an email that agreed, never do-not-contact. */
export function payLinkTargets(c: Pick<Customer, 'doNotContact' | 'contacts'>): {
  method: ContactMethod;
  contactName: string;
}[] {
  if (c.doNotContact) return [];
  return c.contacts.flatMap((k) =>
    k.methods.filter((m) => m.consent && !m.doNotContact).map((method) => ({ method, contactName: k.name })),
  );
}

/** An invoice being watched for a payment: since when, and what it owed then. */
export interface Watch {
  at: string;
  owingCents: number;
}
/** A pay link that went: where to, when, and what was owing when it went. */
export interface Sent extends Watch {
  to: string;
}

/**
 * What a payment recorded on the call leaves to watch (QA-523): each invoice open when the form was
 * opened, with what it owed then, so one it paid in full stays on the list as Paid.
 */
export const watchAfterRecording = (open: readonly Invoice[], at: string): Record<string, Watch> =>
  Object.fromEntries(open.map((i) => [i.id, { at, owingCents: i.balanceCents }]));

/** The rows to show: what's open, then any invoice a link went for or a payment was recorded on, once paid. */
export function shownInvoices(m: Pick<Money, 'invoices'>, watched: Record<string, Watch>): Invoice[] {
  const open = openInvoices(m);
  return [...open, ...m.invoices.filter((i) => watched[i.id] && !open.includes(i))];
}

/**
 * A watched row (a pay link sent, a payment recorded): still waiting, partly paid, or paid, with the
 * latest payment's time (one received since).
 */
export function payState(
  sent: Watch,
  invoice: Pick<Invoice, 'balanceCents'> | undefined,
  payments: readonly Pick<Payment, 'receivedAt' | 'amountCents' | 'status'>[] | null,
): { state: 'waiting' | 'part' | 'paid'; paidCents: number; at: string | null } {
  const balance = invoice?.balanceCents ?? 0;
  const paidCents = Math.max(0, sent.owingCents - balance);
  const after = (payments ?? [])
    .filter((p) => p.status !== 'void' && new Date(p.receivedAt).getTime() >= new Date(sent.at).getTime())
    .sort((a, b) => b.receivedAt.localeCompare(a.receivedAt))[0];
  if (balance <= 0) return { state: 'paid', paidCents, at: after?.receivedAt ?? null };
  if (paidCents > 0) return { state: 'part', paidCents, at: after?.receivedAt ?? null };
  return { state: 'waiting', paidCents: 0, at: null };
}
